import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import DownloadIcon from "@mui/icons-material/Download";

import ReactApexChart from "react-apexcharts";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  Skeleton,
  Grid,
  Stack,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  LinearProgress,
  Popover,
  Drawer,
  Card,
  CardActionArea,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
// import ComplianceChatBot from "../../../assets/Compliance_Chat_bot.svg";
// import AssistantIcon from '@mui/icons-material/Assistant';
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import styles from "./cyber_resilience.module.scss";
import lightlogo from "../../../assets/cyber_resilence_light.svg";
import darklogo from "../../../assets/cyber_resilience_dark.svg";
import CyberResilemce_OnPrem_img from "../../../assets/on_prem_cr_dark.svg";
import CyberResilemce_OnPrem_img_lite from "../../../assets/on_prem_cr_lite.svg";
import Cloud_OnPrem_img from "../../../assets/baseVersion/Cloud_OnPrem_img.svg";
import Cloud_OnPrem_dark_img from "../../../assets/cloud_cr_dark.svg";
import Cve_OnPrem_img from "../../../assets/baseVersion/Cve_OnPrem_img.svg";
import Cve_OnPrem_dark_img from "../../../assets/cve_cr_dark.svg";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import relativeTime from "dayjs/plugin/relativeTime";
import Download_report from "../../../assets/Sort-Decending.svg";
import { toast } from "react-toastify";
import chat_icon from "../../../assets/cyber_resilence_dark.svg";
import { Close as CloseIcon } from "@mui/icons-material";
import { Security as SecurityIcon } from "@mui/icons-material";
import { Timeline as TimelineIcon } from "@mui/icons-material";
import { Event as EventIcon } from "@mui/icons-material";
import { TrendingUp as TrendingUpIcon } from "@mui/icons-material";
import { TrendingDown as TrendingDownIcon } from "@mui/icons-material";
import { Remove as RemoveIcon } from "@mui/icons-material";
import { Assessment as AssessmentIcon } from "@mui/icons-material";
import chat_send from "../../../assets/chat_send.svg";
import eventEmitter from "../../../components/utils/eventEmitter";
import { CyberResilienceRediretions } from "./CyberResilienceRediretions";
import Layer123 from "../../../assets/Layer123.svg";
import { useNavigate } from "react-router-dom";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import useRolePermissions from "../../../shared/hooks/useRolePermissions";
import { ROLES_CONTENT_PROPS } from "../../../shared/constants/constant-data";
import funnel_filter from "../../../assets/funnel_filter.svg";
import { DatePicker } from "@mui/x-date-pickers";

dayjs.extend(isBetween);
dayjs.extend(relativeTime);

// Utility function to format time as "ago" using moment.js
// const formatTimeAgo = (dateTime: string): string => {
//   try {
//     const eventTime = moment(dateTime);
//     const now = moment();
//     const diffInMinutes = now.diff(eventTime, 'minutes');

//     if (diffInMinutes < 1) {
//       return '**Just now**';
//     } else if (diffInMinutes < 60) {
//       return `**${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago**`;
//     } else if (diffInMinutes < 1440) { // Less than 24 hours
//       const hours = Math.floor(diffInMinutes / 60);
//       return `**${hours} hour${hours > 1 ? 's' : ''} ago**`;
//     } else {
//       const days = Math.floor(diffInMinutes / 1440);
//       return `**${days} day${days > 1 ? 's' : ''} ago**`;
//     }
//   } catch (error) {
//     // Fallback to original format if parsing fails
//     return `**${dayjs(dateTime).format('HH:mm:ss')}**`;
//   }
// };
// Fixed tooltip row height and the space taken by its header/score/footer, used to
// list as many events as fit on screen; the rest are in the list opened by clicking the point
const TOOLTIP_ROW_HEIGHT = 24;
const TOOLTIP_CHROME_HEIGHT = 110;
// Gap kept between the tooltip and the edge of the visible area
const TOOLTIP_EDGE_GAP = 8;

// Vertical band of the screen the chart's tooltip can be seen in: the viewport,
// narrowed by every ancestor that clips its overflow
const getTooltipBounds = (baseEl: HTMLElement) => {
  let top = 0;
  let bottom = window.innerHeight;
  for (let el = baseEl.parentElement; el && el !== document.body; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (/hidden|auto|scroll|clip/.test(`${style.overflow} ${style.overflowY}`)) {
      const rect = el.getBoundingClientRect();
      top = Math.max(top, rect.top);
      bottom = Math.min(bottom, rect.bottom);
    }
  }
  return { top: top + TOOLTIP_EDGE_GAP, bottom: bottom - TOOLTIP_EDGE_GAP };
};

// ApexCharts places the tooltip against the plot area only; shift it vertically
// so the whole box stays inside the visible band. The tooltip is
// non-interactive, so landing under the pointer doesn't block hovering.
const keepTooltipInBounds = (baseEl: HTMLElement) => {
  const tooltipEl = baseEl.querySelector<HTMLElement>(".apexcharts-tooltip");
  if (!tooltipEl || !tooltipEl.classList.contains("apexcharts-active")) return;
  const { top, bottom } = getTooltipBounds(baseEl);
  const rect = tooltipEl.getBoundingClientRect();
  let shift = 0;
  if (rect.bottom > bottom) shift = bottom - rect.bottom;
  if (rect.top + shift < top) shift = top - rect.top;
  if (shift) {
    tooltipEl.style.top = `${(parseFloat(tooltipEl.style.top) || 0) + shift}px`;
  }
};

// Saved graph date range. A range ending today is saved as rolling (its
// length in days) so it still ends today on later visits; a range ending on
// an earlier date is kept fixed as picked.
const RANGE_STORAGE_KEY = "cyberResilience_range";
const LEGACY_START_KEY = "cyberResilience_startDate";
const LEGACY_END_KEY = "cyberResilience_endDate";

// What ApexCharts passes to tooltip.custom (only the parts read outside it)
type TooltipArgs = {
  seriesIndex: number;
  dataPointIndex: number;
  w: { globals?: { dom?: { baseEl?: HTMLElement } } };
};

type SavedRange =
  | { mode: "rolling"; days: number }
  | { mode: "fixed"; start: string; end: string };

const loadSavedRange = (): { start: dayjs.Dayjs; end: dayjs.Dayjs } => {
  try {
    const raw = localStorage.getItem(RANGE_STORAGE_KEY);
    if (raw) {
      const saved: SavedRange = JSON.parse(raw);
      if (saved.mode === "rolling" && saved.days >= 0) {
        return { start: dayjs().subtract(saved.days, "day"), end: dayjs() };
      }
      if (saved.mode === "fixed") {
        const start = dayjs(saved.start);
        const end = dayjs(saved.end);
        if (start.isValid() && end.isValid()) return { start, end };
      }
    }
    // Older versions saved fixed dates on every visit, so the end date went
    // stale. Carry their length forward as a rolling range.
    const legacyStart = localStorage.getItem(LEGACY_START_KEY);
    const legacyEnd = localStorage.getItem(LEGACY_END_KEY);
    if (legacyStart && legacyEnd) {
      const days = dayjs(legacyEnd).diff(dayjs(legacyStart), "day");
      if (days >= 0) {
        return { start: dayjs().subtract(days, "day"), end: dayjs() };
      }
    }
  } catch {
    // Storage unavailable or malformed - fall back to the default
  }
  return { start: dayjs().subtract(6, "month"), end: dayjs() };
};

const saveRange = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
  try {
    const saved: SavedRange = end.isSame(dayjs(), "day")
      ? { mode: "rolling", days: end.diff(start, "day") }
      : {
        mode: "fixed",
        start: start.format("YYYY-MM-DD"),
        end: end.format("YYYY-MM-DD"),
      };
    localStorage.setItem(RANGE_STORAGE_KEY, JSON.stringify(saved));
    localStorage.removeItem(LEGACY_START_KEY);
    localStorage.removeItem(LEGACY_END_KEY);
  } catch {
    // Storage unavailable - the range just won't be remembered
  }
};

const formatTimeAgo = (dateTime: string): string => {
  try {
    return dayjs(dateTime).format("HH:mm:ss");
  } catch {
    return `**${dayjs(dateTime).format("HH:mm:ss")}**`;
  }
};

interface ChartDataPoint {
  x: number;
  y: number;
  new_event: boolean;
  date_time: string;
}

interface CustomDayProps
  extends Omit<React.ComponentProps<typeof PickersDay>, "day"> {
  day: dayjs.Dayjs;
  theme: any;
  tempStartDate: dayjs.Dayjs | null;
  tempEndDate: dayjs.Dayjs | null;
}

interface ChatTableRow {
  date: string;
  eventCount: string;
}

const ChatResponseTable: React.FC<{
  data: string;
  isDarkMode: boolean;
  theme: any;
}> = React.memo(({ data, isDarkMode, theme }): React.ReactElement | null => {
  const parseTableData = (result: string): ChatTableRow[] => {
    try {
      if (!result) return [];

      // Look for JSON object in the response
      const jsonMatch = result.match(
        /Day-wise breakdown in json:\s*(\{[\s\S]*?\})/
      );
      if (jsonMatch) {
        try {
          const jsonData = JSON.parse(jsonMatch[1]);
          // Check if JSON object is empty
          if (Object.keys(jsonData).length === 0) {
            return []; // Return empty array to indicate no data
          }
          return Object.entries(jsonData).map(([date, count]) => ({
            date: date,
            eventCount: (count as number).toString(),
          }));
        } catch (jsonError) {
          // Fallback to old parsing method if JSON parsing fails
        }
      }

      // Fallback to old parsing method for backward compatibility
      const lines = result.split("\n");
      return lines
        .filter((line: string) => {
          if (!line) return false;
          if (line.includes("Total count")) return false;
          if (line.includes("Day-wise breakdown")) return false;
          if (line.includes("---")) return false; // Skip separator line
          if (line.includes("date") && line.includes("events count"))
            return false; // Skip header
          return line.includes("|"); // Only include lines with pipe separator
        })
        .map((line: string) => {
          const parts = line
            .split("|")
            .filter(Boolean)
            .map((part: string) => part.trim());
          if (parts.length >= 2) {
            return {
              date: parts[0],
              eventCount: parts[1].split(" ")[0], // Get just the number from "X events"
            };
          }
          return null;
        })
        .filter((row): row is ChatTableRow => row !== null);
    } catch (error) {
      // Error parsing table data
      return [];
    }
  };

  const rows = parseTableData(data);

  // If no table data found, return null to indicate no table should be shown
  if (rows.length === 0) {
    return null;
  }

  return (
    <TableBody>
      {rows.map((row, index) => {
        try {
          let formattedDate = row.date;
          try {
            const dateObj = new Date(row.date);
            if (!isNaN(dateObj.getTime())) {
              formattedDate = dateObj.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              });
            }
          } catch (e) {
            // Date parsing error
          }

          return (
            <TableRow
              key={index}
              sx={{
                height: "52px",
                bgcolor:
                  index % 2 === 0
                    ? "transparent"
                    : isDarkMode
                      ? "rgba(147, 112, 219, 0.03)"
                      : "rgba(147, 112, 219, 0.02)",
                "&:hover": {
                  bgcolor: isDarkMode
                    ? "rgba(147, 112, 219, 0.08)"
                    : "rgba(147, 112, 219, 0.05)",
                },
                transition: "background-color 0.2s",
              }}
            >
              <TableCell
                sx={{
                  borderBottom: `1px solid ${isDarkMode
                    ? "rgba(147, 112, 219, 0.1)"
                    : "rgba(147, 112, 219, 0.05)"
                    }`,
                  color: theme.palette.text.primary,
                  py: 2,
                  height: "52px",
                  fontWeight: 600,
                }}
              >
                {formattedDate}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  borderBottom: `1px solid ${isDarkMode
                    ? "rgba(147, 112, 219, 0.1)"
                    : "rgba(147, 112, 219, 0.05)"
                    }`,
                  color: isDarkMode
                    ? "rgba(158, 158, 158, 0.9)"
                    : "rgba(97, 97, 97, 1)",
                  py: 2,
                  height: "52px",
                }}
              >
                {row.eventCount}
              </TableCell>
            </TableRow>
          );
        } catch (error) {
          // Error rendering row
          return null;
        }
      })}
    </TableBody>
  );
});

const CyberResilienceView: React.FC = () => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [isDateChangeLoading, setIsDateChangeLoading] = useState(false);

  // Default: last 6 months up to today, or the saved range (see loadSavedRange)
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(
    () => loadSavedRange().start
  );

  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(
    () => loadSavedRange().end
  );

  const [timeRange, setTimeRange] = useState<'custom'>('custom');
  const [selectOpen, setSelectOpen] = useState(false);

  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(startDate);
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>(endDate);

  const [appliedFromDate, setAppliedFromDate] = useState<dayjs.Dayjs | null>(startDate);
  const [appliedToDate, setAppliedToDate] = useState<dayjs.Dayjs | null>(endDate);

  const [openFrom, setOpenFrom] = useState(false);
  const [openTo, setOpenTo] = useState(false);

  useEffect(() => {
    if (fromDate && toDate && fromDate.isAfter(toDate, "day")) {
      setFromDate(toDate);
    }
  }, [toDate]);

  const isInvalidRange =
    !fromDate || !toDate || fromDate.isAfter(toDate, "day");

  // Update localStorage when dates change
  useEffect(() => {
    if (startDate && endDate) {
      saveRange(startDate, endDate);
    }
  }, [startDate, endDate]);

  const [allData, setAllData] = useState<ChartDataPoint[]>([
    {
      x: new Date().getTime(),
      y: 0,
      new_event: false,
      date_time: new Date().toISOString(),
    },
  ]);
  const [onPremData, setOnPremData] = useState<ChartDataPoint[]>([
    {
      x: new Date().getTime(),
      y: 0,
      new_event: false,
      date_time: new Date().toISOString(),
    },
  ]);
  const [cloudData, setCloudData] = useState<ChartDataPoint[]>([
    {
      x: new Date().getTime(),
      y: 0,
      new_event: false,
      date_time: new Date().toISOString(),
    },
  ]);
  const [cveData, setCVEData] = useState<ChartDataPoint[]>([
    {
      x: new Date().getTime(),
      y: 0,
      new_event: false,
      date_time: new Date().toISOString(),
    },
  ]);
  const [loading, setLoading] = useState(true);
  const fetchSeqRef = useRef(0);
  // Org's first day with trend data (from the trend API); the date range and
  // pickers don't go earlier than this
  const [firstDataDate, setFirstDataDate] = useState<dayjs.Dayjs | null>(null);
  // Set when the range start is moved up to firstDataDate: the data for the
  // clamped range is what was just fetched, so skip the refetch it triggers
  const skipNextFetchRef = useRef(false);
  const [showContent, setShowContent] = useState(false);
  const [activeDataset, setActiveDataset] = useState<string>("all");
  const [chatOpen, setChatOpen] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [chatResponse, setChatResponse] = useState({
    status: 200,
    message: "Welcome",
    data: {
      result: "",
    },
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tempStartDate, setTempStartDate] = useState<dayjs.Dayjs | null>(null);
  const [tempEndDate, setTempEndDate] = useState<dayjs.Dayjs | null>(null);
  const [selectionStep, setSelectionStep] = useState<"start" | "end">("start");
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);
  const [eventDetailsLoading, setEventDetailsLoading] = useState(false);
  const [trigger, setTrigger] = useState(0);
  const [dataFetched, setDataFetched] = useState(false);
  const [eventClicked, setEventClicked] = useState(false);
  const [eventListOpen, setEventListOpen] = useState(false);
  const [eventListData, setEventListData] = useState<any>(null);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const [chartKey, setChartKey] = useState(0);
  const { hasPermissions } = useRolePermissions();
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [showMiniGraphsInPopover, setShowMiniGraphsInPopover] = useState(false);
  // Use refs to store tooltip data without causing re-renders.
  // Raw event_data per "<dataset>|<date>"; null means the request failed.
  const tooltipCacheRef = useRef<Map<string, any[] | null>>(new Map());
  const tooltipInFlightRef = useRef<Set<string>>(new Set());
  const tooltipTimerRef = useRef<{
    key: string;
    id: ReturnType<typeof setTimeout>;
  } | null>(null);
  // Bumped whenever the cache is cleared so late responses don't repopulate it
  const tooltipCacheGenRef = useRef(0);
  // Event point the tooltip is showing, so a response can redraw it in place
  const tooltipHoverRef = useRef<{ key: string; args: TooltipArgs } | null>(null);
  const customTooltipRef = useRef<((args: TooltipArgs) => string) | null>(null);
  // Abort controller to cancel inflight event list requests so only the latest click runs
  const tooltipAbortRef = useRef<AbortController | null>(null);

  const isDarkMode = theme.palette.mode === "dark";

  const handleDateClose = () => {
    setAnchorEl(null);
    setSelectionStep("start");
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleEventCardClick = (event: any) => {
    if (event.new_event === false) return;
    if (!isEventClickable(event)) {
      return;
    }
    setSelectedEvent(event);
    // setEventDialogOpen(false);
    setEventClicked(true);
  };

  const handleDateApply = () => {
    if (tempStartDate && tempEndDate) {
      setIsDateChangeLoading(true); // Set loading when dates change
      setStartDate(tempStartDate);
      setEndDate(tempEndDate);
      setTimeRange('custom');
      setDataFetched(false); // Force refetch
      handleDateClose();
    }
  };

  const handleDateSelect = (date: dayjs.Dayjs | null) => {
    if (selectionStep === "start") {
      setTempStartDate(date);
      setSelectionStep("end");
    } else {
      setTempEndDate(date);
    }
  };

  const open = Boolean(anchorEl);

  const getTooltipKey = (date: string) => `${activeDataset}|${date}`;

  const getDailyEventRequest = (date: string) => {
    let url = END_POINTS.CYBERRESILIENCE_DASHBOARD_DAILY;
    const payload: any = {
      date: date,
      // Same timezone the trend graph is fetched with, so the day windows line up
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    if (activeDataset === "cve") {
      url = END_POINTS.CYBERRESILIENCE_DASHBOARD_CVE;
      payload.module = "on_premise";
    } else if (activeDataset === "onPrem") {
      url = END_POINTS.CYBERRESILIENCE_DASHBOARD_ON_PREM;
      payload.module = "on_premise";
    } else if (activeDataset === "cloud") {
      url = END_POINTS.CYBERRESILIENCE_DASHBOARD_ON_PREM;
      payload.module = "cloud";
    }
    return { url, payload };
  };

  const handleEventClick = async (date: string) => {
    // Cancel any in-progress tooltip/event list request; latest click should win
    if (tooltipAbortRef.current) {
      try { tooltipAbortRef.current.abort(); } catch (_) { }
      tooltipAbortRef.current = null;
    }
    try {
      const key = getTooltipKey(date);
      // Reuse what the hover tooltip already fetched for this date
      let eventData = tooltipCacheRef.current.get(key);
      if (!eventData) {
        const controller = new AbortController();
        tooltipAbortRef.current = controller;
        const { url, payload } = getDailyEventRequest(date);
        const response = await axiosInstance.post(url, payload, { signal: (controller as any).signal });
        const fetched: any[] = response.data.data.event_data || [];
        tooltipCacheRef.current.set(key, fetched);
        eventData = fetched;
      }
      const formattedData = {
        date: dayjs(date).format("MM/DD/YYYY"),
        data: (eventData as any[]).map((event: any) => ({
          time: formatTimeAgo(event.event_date),
          security_score: parseFloat(event.current_score).toFixed(2),
          score_change: parseFloat(event.score_difference).toFixed(2),
          event_type: event.event_name,
          event_code: event.event_code,
          referenceID: event.reference_id,
          originalEvent: event, // Keep the original event data for details
        })),
      };
      setEventListData(formattedData);
      setEventListOpen(true);
    } catch (error) {
      console.error("Failed to fetch event data:", error);
    } finally {
      // Clear the abort ref after this request completes/aborts
      tooltipAbortRef.current = null;
    }
  };

  // Helper function to check if event is clickable
  const isEventClickable = (event: any) => {
    return !(
      event.event_code === "asset_recom" ||
      event.event_code === "device_decom" ||
      event.event_code === "asset_decom" ||
      event.event_code === "device_recom"
    );
  };

  const handleEventItemClick = (event: any) => {
    if (event.new_event === false) return;
    if (!isEventClickable(event)) {
      return;
    }

    // Validate that we have the required data
    if (!event.originalEvent) {
      return;
    }

    // Fix field name mapping - use reference_id as referenceID
    const eventWithCorrectFields = {
      ...event.originalEvent,
      referenceID:
        event.originalEvent.reference_id || event.originalEvent.referenceID,
    };

    setSelectedEvent(eventWithCorrectFields);
    setEventClicked(true);
    setEventListOpen(false);
  };

  // ApexCharts only redraws the tooltip on mouse move. When a date's events
  // arrive while the pointer rests on that point, redraw the open tooltip so it
  // doesn't stay on "Loading events..." until the mouse moves.
  const refreshOpenTooltip = (key: string) => {
    const hover = tooltipHoverRef.current;
    if (!hover || hover.key !== key || !customTooltipRef.current) return;
    const tooltipEl = hover.args.w.globals?.dom?.baseEl?.querySelector<HTMLElement>(
      ".apexcharts-tooltip"
    );
    if (!tooltipEl || !tooltipEl.classList.contains("apexcharts-active")) return;
    tooltipEl.innerHTML = customTooltipRef.current(hover.args);
    keepTooltipInBounds(hover.args.w.globals!.dom!.baseEl!);
  };

  // ApexCharts calls the custom tooltip on every mouse move, so this must be
  // idempotent: one request per dataset+date, only after the pointer rests on
  // the point briefly. Requests are not aborted - the server keeps running an
  // aborted query anyway, so let it finish and cache the result.
  const fetchTooltipEventData = (date: string) => {
    const key = getTooltipKey(date);
    if (
      tooltipCacheRef.current.has(key) ||
      tooltipInFlightRef.current.has(key) ||
      tooltipTimerRef.current?.key === key
    ) {
      return;
    }
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current.id);
    }
    const id = setTimeout(async () => {
      tooltipTimerRef.current = null;
      const gen = tooltipCacheGenRef.current;
      tooltipInFlightRef.current.add(key);
      try {
        const { url, payload } = getDailyEventRequest(date);
        const response = await axiosInstance.post(url, payload);
        if (gen === tooltipCacheGenRef.current) {
          tooltipCacheRef.current.set(key, response.data.data.event_data || []);
          refreshOpenTooltip(key);
        }
      } catch (error) {
        console.error("Failed to fetch tooltip event data:", error);
        // Cache the failure so mouse moves don't keep re-requesting it
        if (gen === tooltipCacheGenRef.current) {
          tooltipCacheRef.current.set(key, null);
          refreshOpenTooltip(key);
        }
      } finally {
        tooltipInFlightRef.current.delete(key);
      }
    }, 250);
    tooltipTimerRef.current = { key, id };
  };

  // const fetchTooltipEventData = async (date: string) => {
  //   // Cancel any in-progress request; latest request should win
  //   if (tooltipAbortRef.current) {
  //     try { tooltipAbortRef.current.abort(); } catch (_) {}
  //     tooltipAbortRef.current = null;
  //   }

  //   // If we already have data for this date and dataset, skip fetching
  //   if (currentTooltipDateRef.current === date && tooltipDataRef.current) {
  //     return;
  //   }

  //   tooltipLoadingRef.current = true;
  //   currentTooltipDateRef.current = date;

  //   try {
  //     const controller = new AbortController();
  //     tooltipAbortRef.current = controller;
  //     let url = END_POINTS.CYBERRESILIENCE_DASHBOARD_DAILY;
  //     let payload: any = {
  //       date: date,
  //       timezone: "Asia/Calcutta",
  //     };
  //     if (activeDataset === "cve") {
  //       url = END_POINTS.CYBERRESILIENCE_DASHBOARD_CVE;
  //       payload.module = "on_premise";
  //     } else if (activeDataset === "onPrem") {
  //       url = END_POINTS.CYBERRESILIENCE_DASHBOARD_ON_PREM;
  //       payload.module = "on_premise";
  //     } else if (activeDataset === "cloud") {
  //       url = END_POINTS.CYBERRESILIENCE_DASHBOARD_ON_PREM;
  //       payload.module = "cloud";
  //     } else {
  //       url = END_POINTS.CYBERRESILIENCE_DASHBOARD_DAILY;
  //     }
  //     const response = await axiosInstance.post(url, payload, { signal: (controller as any).signal });
  //     const formattedData = {
  //       date: dayjs(date).format("DD[th] MMM, YYYY"),
  //       data: response.data.data.event_data.map((event: any) => ({
  //         time: formatTimeAgo(event.event_date),
  //         security_score: parseFloat(event.current_score).toFixed(2),
  //         score_change: parseFloat(event.score_difference).toFixed(2),
  //         event_type: event.event_name,
  //         event_code: event.event_code,
  //         referenceID: event.reference_id,
  //       })),
  //     };
  //     tooltipDataRef.current = formattedData;
  //   } catch (error) {
  //     console.error("Failed to fetch tooltip event data:", error);
  //     tooltipDataRef.current = null;
  //   } finally {
  //     tooltipLoadingRef.current = false;
  //     // Clear the abort ref after this request completes/aborts
  //     tooltipAbortRef.current = null;
  //   }
  // };

  // Memoize fetchTooltipEventData to prevent chart re-renders
  const memoizedFetchTooltipEventData = useCallback(fetchTooltipEventData, [
    activeDataset,
  ]);

  const handleDownload = async () => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await axiosInstance.post(
        "/securityscan/reports/cyber-resilience-trend",
        {
          type: "excel",
          from_date: startDate?.format("YYYY-MM-DD"),
          to_date: endDate?.format("YYYY-MM-DD"),
          timezone: userTimezone,
        },
        {
          responseType: "blob",
        }
      );

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `cyber-resilience-report-${startDate?.format(
          "YYYY-MM-DD"
        )}-to-${endDate?.format("YYYY-MM-DD")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download report");
    }
  };

  const getMainChartData = () => {
    switch (activeDataset) {
      case "cloud":
        return cloudData;
      case "cve":
        return cveData;
      case "onPrem":
        return onPremData;
      case "all":
      default:
        return allData;
    }
  };

  // The trend API pads the days before the first snapshot with zero scores.
  // Start the line at the org's first data date so it fills the chart width
  // instead of sitting on a flat zero run. Without that date (older API),
  // fall back to dropping the leading zero padding.
  const mainChartData = useMemo((): ChartDataPoint[] => {
    const data = getMainChartData();
    if (firstDataDate) {
      const first = firstDataDate.format("YYYY-MM-DD");
      return data.filter((p: ChartDataPoint) => p.date_time.slice(0, 10) >= first);
    }
    const firstReal = data.findIndex((p: ChartDataPoint) => p.y !== 0 || p.new_event);
    return firstReal > 0 ? data.slice(firstReal) : data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDataset, allData, cloudData, cveData, onPremData, firstDataDate]);

  // Fit the y-axis to the data (with a little headroom) instead of a fixed
  // 0-based scale that flattens small score changes into a straight line.
  // ApexCharts passes the data's min/max to these functions.
  const yAxisBounds = useMemo(() => {
    if (!mainChartData.length) return null;
    const ys = mainChartData.map((p) => p.y);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const pad = Math.max((maxY - minY) * 0.15, 2);
    const cap = maxY <= 100 ? 100 : Infinity;
    return {
      min: (dataMin: number) => Math.max(0, Math.floor(dataMin - pad)),
      max: (dataMax: number) => Math.min(cap, Math.ceil(dataMax + pad)),
      tickAmount: 5,
    };
  }, [mainChartData]);

  // A single point has no x extent; give it a day around it so it isn't stretched
  const xAxisBounds = useMemo(() => {
    if (mainChartData.length !== 1) return null;
    const halfDay = 12 * 60 * 60 * 1000;
    return { min: mainChartData[0].x - halfDay, max: mainChartData[0].x + halfDay };
  }, [mainChartData]);

  // Chart event handlers read these refs so the options object doesn't have to
  // be rebuilt (and the chart redrawn) on every render
  const mainChartDataRef = useRef<ChartDataPoint[]>(mainChartData);
  mainChartDataRef.current = mainChartData;
  const handleEventClickRef = useRef(handleEventClick);
  handleEventClickRef.current = handleEventClick;

  const getDatasetDisplayName = () => {
    switch (activeDataset) {
      case "cloud":
        return "Cloud";
      case "cve":
        return "CVE";
      case "onPrem":
        return "On-prem";
      case "all":
      default:
        return "All";
    }
  };

  // Pointer cursor over event points. The tooltip stays non-interactive
  // (ApexCharts default): an interactive tooltip sitting under the pointer
  // swallows mouse moves, so the chart can't move on to the next point.
  // Clicking an event point opens the full event list instead.
  useEffect(() => {
    const styleId = 'cyber-resilience-tooltip-styles';
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) existingStyle.remove();

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .has-active-event,
      .has-active-event * {
        cursor: pointer !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [isDarkMode]);

  // Stable tooltip function to prevent chart re-renders
  const customTooltip = useCallback(
    function ({ seriesIndex, dataPointIndex, w }: any) {
      const dataPoint = w.config.series[seriesIndex].data[dataPointIndex];
      const score = dataPoint.y;
      const hasNewEvent = dataPoint.new_event;

      if (w.globals?.dom?.baseEl) {
        if (hasNewEvent) {
          w.globals.dom.baseEl.classList.add("has-active-event");
          w.globals.dom.baseEl.style.cursor = "pointer";
        } else {
          w.globals.dom.baseEl.classList.remove("has-active-event");
          w.globals.dom.baseEl.style.cursor = "default";
        }
      }

      if (hasNewEvent) {
        const dateStr = dayjs(dataPoint.x).format("YYYY-MM-DD");
        const tooltipKey = getTooltipKey(dateStr);
        const hasCachedEvents = tooltipCacheRef.current.has(tooltipKey);
        const cachedEvents = tooltipCacheRef.current.get(tooltipKey);
        tooltipHoverRef.current = {
          key: tooltipKey,
          args: { seriesIndex, dataPointIndex, w },
        };

        // No-op if this date is already cached, in flight or scheduled
        if (!hasCachedEvents) {
          memoizedFetchTooltipEventData(dateStr);
        }

        // Always show a consistent tooltip structure for new events
        const baseTooltip = `
        <div class="apexcharts-tooltip-box" style="padding: 12px; font-family: inherit; max-width: 400px;">
          <div style="margin-bottom: 12px; font-weight: bold; color: ${isDarkMode ? "#fff" : "#000"
          }; font-size: 14px;">
            Security Score on ${dayjs(dataPoint.x).format("MM/DD/YYYY")}
          </div>
          <div style="display: flex; align-items: center; margin-bottom: 8px">
            <span style="display: inline-block; width: 8px; height: 8px; background: #FF6B00; border-radius: 50%; margin-right: 8px;"></span>
            <span>Security Rating: ${score.toFixed(0)}</span>
          </div>
      `;



        // Show event data in tooltip if we have data for this date
        if (cachedEvents) {
          // The tooltip isn't scrollable (the pointer can't reach it), so list as
          // many of the latest events as fit on screen and count the rest as "+N more"
          const baseEl: HTMLElement | undefined = w.globals?.dom?.baseEl;
          const bounds = baseEl ? getTooltipBounds(baseEl) : null;
          const availableHeight = bounds ? bounds.bottom - bounds.top : 300;
          const rowsThatFit = Math.floor(
            (availableHeight - TOOLTIP_CHROME_HEIGHT) / TOOLTIP_ROW_HEIGHT
          );
          const maxRows = Math.max(1, rowsThatFit);
          const hiddenCount = Math.max(0, cachedEvents.length - maxRows);
          const eventsHtml = cachedEvents
            .slice(-maxRows)
            .map((rawEvent: any) => ({
              time: formatTimeAgo(rawEvent.event_date),
              security_score: parseFloat(rawEvent.current_score).toFixed(2),
              score_change: parseFloat(rawEvent.score_difference).toFixed(2),
              event_type: rawEvent.event_name,
              event_code: rawEvent.event_code,
            }))
            .map((event: any) => {
              const getEventType = (eventCode: string, eventName: string) => {
                if (eventCode === "new_scan") return "New Scan";
                if (eventCode === "rescan") return "Re Scan";
                if (eventName.includes("Risk Accepted")) return "Risk Accepted";
                if (eventName.includes("Risk Unaccepted"))
                  return "Risk Unaccepted";
                if (eventName.includes("Device Decommissioned"))
                  return "Device Decommissioned";
                if (eventName.includes("Device Deleted"))
                  return "Device Deleted";
                if (eventName.includes("Master file Update"))
                  return "Master file Update";
                if (eventName.includes("Asset Critacality"))
                  return "Asset Critacality";
                return eventName.split("-")[0].trim();
              };

              const eventType = getEventType(
                event.event_code,
                event.event_type
              );
              const scoreChange = event.score_change;
              const hasScore = scoreChange !== 0;

              return `
            <div style="display: flex; align-items: center; gap: 8px; height: ${TOOLTIP_ROW_HEIGHT}px; box-sizing: border-box; white-space: nowrap; border-bottom: 1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
                };">
              <span style="color: ${isDarkMode ? "#b3b3b3" : "#666"
                }; font-size: 12px; min-width: 60px;">${event.time}</span>
              ${hasScore
                  ? `<span style="color: ${isDarkMode ? "#fff" : "#000"
                  }; font-size: 12px; font-weight: 500;">- ${event.security_score
                  }</span>`
                  : ""
                }
              ${hasScore
                  ? `
                <span style="display: flex; align-items: center; gap: 4px; min-width: 30px;">
                  <span style="color: ${scoreChange > 0 ? "#4caf50" : "#f44336"
                  }; font-size: 14px;">${scoreChange > 0 ? "▲" : "▼"}</span>
                  <span style="color: ${scoreChange > 0 ? "#4caf50" : "#f44336"
                  }; font-size: 12px; font-weight: 700;">${parseFloat(
                    scoreChange
                  ).toFixed(2)}</span>
                </span>
              `
                  : ""
                }
              <span style="color: ${isDarkMode ? "#fff" : "#000"
                }; font-size: 12px; font-weight: 500; min-width: 0; overflow: hidden; text-overflow: ellipsis;">(${eventType})</span>
            </div>
          `;
            })
            .join("");

          return (
            baseTooltip +
            `
          <div>
            ${eventsHtml}
          </div>
          ${hiddenCount
              ? `<div style="padding-top: 8px; font-size: 12px; color: ${isDarkMode ? "#b3b3b3" : "#666"
              };">+${hiddenCount} more · click the point to see all</div>`
              : ""
            }
        </div>
        `
          );
        } else if (hasCachedEvents) {
          // Request for this date failed
          return (
            baseTooltip +
            `
          <div style="display: flex; align-items: center; padding-top: 8px; border-top: 1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
            }">
            <span style="color: #FF0000;">Unable to load events</span>
          </div>
        </div>
        `
          );
        } else {
          // Show loading state while fetching data
          return (
            baseTooltip +
            `
          <div style="display: flex; align-items: center; padding-top: 8px; border-top: 1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
            }">
            <span style="display: inline-block; width: 8px; height: 8px; background: #FF0000; border-radius: 50%; margin-right: 8px;"></span>
            <span style="color: #FF0000;">Loading events...</span>
          </div>
        </div>
        `
          );
        }
      }

      tooltipHoverRef.current = null;

      // Points without events still need a tooltip: returning undefined makes
      // ApexCharts throw on every mouse move and the tooltip freezes in place
      return `
        <div class="apexcharts-tooltip-box" style="padding: 12px; font-family: inherit;">
          <div style="margin-bottom: 8px; font-weight: bold; color: ${isDarkMode ? "#fff" : "#000"
        }; font-size: 14px;">
            Security Score on ${dayjs(dataPoint.x).format("MM/DD/YYYY")}
          </div>
          <div style="display: flex; align-items: center;">
            <span style="display: inline-block; width: 8px; height: 8px; background: #FF6B00; border-radius: 50%; margin-right: 8px;"></span>
            <span>Security Rating: ${score.toFixed(0)}</span>
          </div>
        </div>
      `;
    },
    [isDarkMode, activeDataset, memoizedFetchTooltipEventData]
  );
  customTooltipRef.current = customTooltip;

  // Memoize chart options to prevent zoom reset on tooltip data changes
  const mainChartOptions = useMemo(
    (): ApexCharts.ApexOptions => ({
      chart: {
        type: "area",
        stacked: false,
        height: isTablet ? 250 : 350,
        // No zoom: the graph always shows the selected date range. (ApexCharts 4
        // wheel zoom also preventDefaults the wheel, blocking page scroll.)
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        background: "transparent",
        events: {
          dataPointSelection: function (
            event,
            chartContext,
            config
          ) {
            const dataPointIndex = config?.dataPointIndex;
            if (dataPointIndex !== undefined && dataPointIndex >= 0) {
              const dataPoint = mainChartDataRef.current[dataPointIndex];
              if (dataPoint?.new_event) {
                const date = dayjs(dataPoint.date_time).format("YYYY-MM-DD");
                handleEventClickRef.current(date);
              }
            }
          },
          click: function (
            event,
            chartContext,
            config
          ) {
            const dataPointIndex = config?.dataPointIndex;
            if (dataPointIndex !== undefined && dataPointIndex >= 0) {
              const dataPoint = mainChartDataRef.current[dataPointIndex];
              if (dataPoint?.new_event) {
                const date = dayjs(dataPoint.date_time).format("YYYY-MM-DD");
                handleEventClickRef.current(date);
              }
            }
          },
          mouseMove: function (_event, chartContext) {
            // After ApexCharts has positioned the tooltip for this move
            const baseEl = chartContext.w?.globals?.dom?.baseEl;
            if (baseEl) requestAnimationFrame(() => keepTooltipInBounds(baseEl));
          },
          mouseLeave: function (event, chartContext) {
            tooltipHoverRef.current = null;
            if (chartContext.w?.globals?.dom?.baseEl) {
              chartContext.w.globals.dom.baseEl.classList.remove("has-active-event");
              chartContext.w.globals.dom.baseEl.style.cursor = 'default';
            }
          },
        },
        animations: { enabled: false },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        // The score holds until an event changes it, so draw steps: a smooth
        // curve shows slopes (and overshoot) between days where nothing happened
        curve: "stepline",
        width: 2,
        colors: ["#FF6B00"],
      },
      markers: {
        size: 0,
        hover: {
          size: 5,
        },
      },
      states: {
        hover: {
          filter: {
            type: "none",
          },
        },
        active: {
          filter: {
            type: "none",
          },
        },
      },
      tooltip: {
        shared: true,
        x: {
          show: false,
        },
        y: {
          formatter: (val) => `${val.toFixed(0)}`,
        },
        marker: {
          show: true,
        },
        theme: isDarkMode ? "dark" : "light",
        custom: customTooltip,

        intersect: false,
        // Hide default tooltip when customTooltip returns empty string
        enabled: true,
      },
      title: {
        text: "",
        align: "left",
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          inverseColors: false,
          opacityFrom: isDarkMode ? 0.6 : 0.7,
          opacityTo: isDarkMode ? 0.1 : 0.2,
          stops: [0, 50, 100],
          colorStops: [
            {
              offset: 0,
              color: isDarkMode
                ? "rgba(255, 107, 0, 0.7)"
                : "rgba(255, 107, 0, 0.8)",
              opacity: 1,
            },
            {
              offset: 50,
              color: isDarkMode
                ? "rgba(255, 107, 0, 0.4)"
                : "rgba(255, 107, 0, 0.5)",
              opacity: 0.5,
            },
            {
              offset: 100,
              color: isDarkMode
                ? "rgba(255, 107, 0, 0.1)"
                : "rgba(255, 107, 0, 0.2)",
              opacity: 0.1,
            },
          ],
        },
      },
      grid: {
        borderColor: isDarkMode
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.1)",
        strokeDashArray: 3,
        padding: {
          right: 20,
          left: 10,
        },
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      xaxis: {
        type: "datetime",
        ...(xAxisBounds || {}),
        tooltip: {
          enabled: false,
        },
        labels: {
          style: {
            colors: isDarkMode ? "#fff" : "#000",
            fontSize: "12px",
          },
        },
        axisBorder: {
          color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
        },
        axisTicks: {
          color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
        },
      },
      yaxis: {
        ...(yAxisBounds || {}),
        labels: {
          style: {
            colors: isDarkMode ? "#fff" : "#000",
            fontSize: "12px",
          },
          // One decimal on a fitted narrow range so tick labels don't repeat
          formatter: (val) => (Number.isInteger(val) ? `${val}` : val.toFixed(1)),
        },
        axisBorder: {
          color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
        },
        axisTicks: {
          color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
        },
      },
      legend: {
        show: false,
      },
      noData: {
        text: "No data available",
        align: "center",
        verticalAlign: "middle",
        offsetX: 0,
        offsetY: 0,
        style: {
          color: isDarkMode ? "#fff" : "#000",
          fontSize: "14px",
          fontFamily: "inherit",
        },
      },
    }),
    [isTablet, isDarkMode, customTooltip, xAxisBounds, yAxisBounds]
  );

  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true);
      setTrigger((prev) => prev + 1);
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);



  const fetchDatasetData = async (
    dataset: string,
    startDate: dayjs.Dayjs | null,
    endDate: dayjs.Dayjs | null
  ) => {
    // Only the latest request may end the loading state; an older response
    // arriving late (quick dataset/date switches) must not reveal the chart early
    const seq = ++fetchSeqRef.current;
    try {
      setLoading(true);
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const transformData = (
        apiResponse: any,
        isForCve: boolean = false
      ): ChartDataPoint[] => {
        const graphData = apiResponse?.data?.graph_data || [];
        return graphData.map((item: any) => ({
          x: new Date(item.date_time).getTime(),
          y: isForCve ? item.cve_score || 0 : item.score || item.security_rating || 0,
          new_event: item.new_event || false,
          date_time: item.date_time,
        }));
      };

      const transformAllData = (
        apiResponse: any,
        isForCve: boolean = false
      ): ChartDataPoint[] => {
        const graphData = apiResponse?.data?.graph_data || [];
        return graphData.map((item: any) => ({
          x: new Date(item.date_time).getTime(),
          y: isForCve ? item.cve_score || 0 : item.score || item.intelli_score || 0,
          new_event: item.new_event || false,
          date_time: item.date_time,
        }));
      };

      const basePayload = {
        duration: "custom_date_range",
        start_date: startDate?.format("YYYY-MM-DD"),
        end_date: endDate?.format("YYYY-MM-DD"),
        timezone: userTimezone,
      };

      let response;
      switch (dataset) {
        case "onPrem":
          response = await axiosInstance.post(
            END_POINTS.DASHBOARD_CYBER_RESILIENCE_TREND,
            { ...basePayload, module: "on_premise", cve: false, intelli_score: false, rating: true }
          );
          if (response.data) {
            setOnPremData(transformData(response.data));
            // setCVEData(transformData(response.data, true));
          }
          break;

        case "cve":
          response = await axiosInstance.post(
            END_POINTS.DASHBOARD_CYBER_RESILIENCE_TREND,
            { ...basePayload, module: "on_premise", cve: true, intelli_score: false, rating: false }
          );
          if (response.data) {
            //setOnPremData(transformData(response.data));
            setCVEData(transformData(response.data, true));
          }
          break;

        case "cloud":
          response = await axiosInstance.post(
            END_POINTS.DASHBOARD_CYBER_RESILIENCE_TREND,
            { ...basePayload, module: "cloud", cve: false, intelli_score: false, rating: true }
          );
          if (response.data) {
            setCloudData(transformData(response.data));
          }
          break;

        case "all":
        default:
          response = await axiosInstance.post(
            END_POINTS.DASHBOARD_CYBER_RESILIENCE_TREND,
            { ...basePayload, module: "all", cve: false, intelli_score: true, rating: false }
          );
          if (response.data) {
            setAllData(transformAllData(response.data));
          }
          break;
      }

      if (seq === fetchSeqRef.current) {
        const firstDate = response?.data?.data?.first_data_date;
        setFirstDataDate(firstDate ? dayjs(firstDate) : null);
        setChartKey((prev) => prev + 1);
        setDataFetched(true);
      }
    } catch (error) {
      toast.error("Failed to fetch cyber resilience data");
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
        setIsDateChangeLoading(false);
        setShowContent(true);
      }
    }
  };


  // Initial load
  // useEffect(() => {
  //   if (startDate && endDate) {
  //     fetchDatasetData(activeDataset, startDate, endDate);
  //   }
  // }, []);

  // // Re-fetch when dataset changes
  // useEffect(() => {
  //   if (startDate && endDate) {
  //     fetchDatasetData(activeDataset, startDate, endDate);
  //   }
  // }, [activeDataset]);

  // // Re-fetch when dates change
  // useEffect(() => {
  //   if (startDate && endDate) {
  //     fetchDatasetData(activeDataset, startDate, endDate);
  //   }
  // }, [startDate, endDate]);
  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    if (startDate && endDate && activeDataset) {
      fetchDatasetData(activeDataset, startDate, endDate);
    }
  }, [startDate, endDate, activeDataset, trigger]);

  // Don't let the range start before the org had any data (default 6 months
  // or a saved range can reach back past it). Left alone if the whole range
  // is before the first data, so start never passes end.
  useEffect(() => {
    if (!firstDataDate || !startDate || !endDate) return;
    if (
      startDate.isBefore(firstDataDate, "day") &&
      !endDate.isBefore(firstDataDate, "day")
    ) {
      skipNextFetchRef.current = true;
      setStartDate(firstDataDate);
      setAppliedFromDate(firstDataDate);
      setFromDate(firstDataDate);
    }
  }, [firstDataDate, startDate, endDate]);

  // Graph data was refetched - drop cached tooltip events and any pending hover fetch
  useEffect(() => {
    tooltipCacheRef.current.clear();
    tooltipCacheGenRef.current += 1;
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current.id);
        tooltipTimerRef.current = null;
      }
    };
  }, [startDate, endDate, activeDataset, trigger]);



  useEffect(() => {
    if (
      onPremData.length === 0 &&
      cloudData.length === 0 &&
      cveData.length === 0 &&
      allData.length === 0
    ) {
      const now = new Date().getTime();
      const initialPoint: ChartDataPoint = {
        x: now,
        y: 0,
        new_event: false,
        date_time: new Date(now).toISOString(),
      };
      setAllData([initialPoint]);
      setOnPremData([initialPoint]);
      setCloudData([initialPoint]);
      setCVEData([initialPoint]);
    }
  }, []);

  const mainSeries = useMemo(() => [
    {
      name: getDatasetDisplayName(),
      data: mainChartData.map((point: any) => ({
        x: point.x,
        y: point.y,
        new_event: point.new_event,
        date_time: point.date_time,
        marker: point.new_event
          ? {
            size: 6,
            fillColor: "#FF0000",
            strokeColor: "#fff",
            strokeWidth: 2,
          }
          : undefined,
      })),
    },
  ], [mainChartData, activeDataset]);

  // While the graph reloads, keep showing the last loaded graph (dimmed, under
  // a spinner) rather than the new dataset's stale data or an empty area
  const isChartLoading = isDateChangeLoading || loading;
  const shownChartRef = useRef({ options: mainChartOptions, series: mainSeries });
  if (!isChartLoading) {
    shownChartRef.current = { options: mainChartOptions, series: mainSeries };
  }

  const allMiniSeries = [
    {
      name: "All",
      data: allData,
    },
  ];

  const onPremMiniSeries = [
    {
      name: "On-prem",
      data: onPremData,
    },
  ];

  const cloudMiniSeries = [
    {
      name: "Cloud",
      data: cloudData,
    },
  ];

  const cveMiniSeries = [
    {
      name: "CVE",
      data: cveData,
    },
  ];

  const getLatestScore = (data: ChartDataPoint[]) => {
    return data.length > 0 ? Math.floor(data[data.length - 1].y) : 0;
  };

  const handleDatasetChange = (dataset: string) => {
    if (dataset === activeDataset) return;
    // Mark loading in the same render as the switch, so the chart never
    // flashes the new dataset's previous data before the fetch starts
    setLoading(true);
    setActiveDataset(dataset);
  };

  // Effect to ensure active dataset has data, if not switch to one that does
  // React.useEffect(() => {
  //   if (!hasSectionData(activeDataset)) {
  //     // Find first dataset that has data
  //     const datasets = ["all", "onPrem", "cloud", "cve"];
  //     const validDataset = datasets.find((dataset) => hasSectionData(dataset));
  //     if (validDataset && validDataset !== activeDataset) {
  //       setActiveDataset(validDataset);
  //     }
  //   }
  // }, [allData, onPremData, cloudData, cveData, activeDataset]);

  const handleCardClick = async (text: string) => {
    setInputValue(text);

    if (!text.trim()) return;

    setIsLoadingChat(true);
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await axiosInstance.get(END_POINTS.SECURITY_SCAN_CHAT, {
        params: {
          query: text.trim(),
          timezone: userTimezone,
        },
        paramsSerializer: (params) => {
          return Object.entries(params)
            .map(([key, value]) => `${key}=${value}`)
            .join("&");
        },
      });

      if (response.data) {
        // Reset the state before setting new data
        setChatResponse({
          status: 200,
          message: "Welcome",
          data: {
            result: "",
          },
        });

        // Set timeout to ensure state is reset before setting new data
        setTimeout(() => {
          setChatResponse(response.data);
        }, 0);
      }
    } catch (error) {
      toast.error("Error fetching chat response:");
      setChatResponse({
        status: 500,
        message: "Error",
        data: {
          result: "Sorry, there was an error processing your request.",
        },
      });
    } finally {
      setIsLoadingChat(false);
    }
  };

  const handleInputSubmit = () => {
    if (inputValue.trim()) {
      handleCardClick(inputValue);
    }
  };

  const handleClose = () => {
    setChatOpen(false);
    setInputValue("");
    // Reset chat response to initial state
    setChatResponse({
      status: 200,
      message: "Welcome",
      data: {
        result: "",
      },
    });
  };

  const handleBackToWelcome = () => {
    setInputValue("");
    // Reset chat response to initial state
    setChatResponse({
      status: 200,
      message: "Welcome",
      data: {
        result: "",
      },
    });
  };

  // Function to extract summary from chat response
  const extractSummary = (result: string): string | null => {
    if (!result) return null;
    // Try different summary formats
    const summaryMatch1 = result.split("\nSummary:\n")[1];
    if (summaryMatch1) return summaryMatch1.trim();

    const summaryMatch2 = result.split("\nSummary: ")[1];
    if (summaryMatch2) return summaryMatch2.trim();

    return null;
  };

  // Function to check if response has summary
  const hasSummary = (result: string): boolean => {
    return result.includes("\nSummary:\n") || result.includes("\nSummary: ");
  };

  // Function to check if response has table data
  const hasTableData = (result: string): boolean => {
    if (!result) return false;
    const lines = result.split("\n");
    // Check for JSON format first
    if (result.includes("Day-wise breakdown in json:")) {
      const jsonMatch = result.match(
        /Day-wise breakdown in json:\s*(\{[\s\S]*?\})/
      );
      if (jsonMatch) {
        try {
          const jsonData = JSON.parse(jsonMatch[1]);
          // Return false if JSON object is empty
          return Object.keys(jsonData).length > 0;
        } catch (error) {
          return false;
        }
      }
      return false;
    }
    // Check for old pipe-separated format
    return lines.some((line) => line.includes("|"));
  };

  // Function to extract total count from response
  const extractTotalCount = (result: string): string | null => {
    if (!result) return null;
    const totalCountMatch = result
      .split("\n")
      .find(
        (line) =>
          line.toLowerCase().includes("total count") ||
          line.toLowerCase().includes("total events") ||
          line.toLowerCase().includes("total scans") ||
          line.toLowerCase().includes("total accepted risk")
      );
    return totalCountMatch?.trim() || null;
  };

  // Function to check if response indicates no data
  const hasNoData = (result: string): boolean => {
    if (!result) return false;
    // Check for empty JSON object
    if (result.includes("Day-wise breakdown in json: {}")) {
      return true;
    }
    // Check for "No matching events found" or similar messages
    if (
      result.toLowerCase().includes("no matching events found") ||
      result.toLowerCase().includes("no data found") ||
      result.toLowerCase().includes("no events found")
    ) {
      return true;
    }
    return false;
  };

  // Add event listener for tooltip clicks
  useEffect(() => {
    const handleTooltipClick = (event: CustomEvent) => {
      if (event.detail.new_event === false) return;
      const date = dayjs(event.detail).format("YYYY-MM-DD");
      handleEventClick(date);
    };

    document.addEventListener(
      "tooltipClick",
      handleTooltipClick as EventListener
    );

    return () => {
      document.removeEventListener(
        "tooltipClick",
        handleTooltipClick as EventListener
      );
    };
  }, [trigger]);

  const getSectionData = (section: string) => {
    const sectionMap = {
      all: {
        name: "All",
        icon: isDarkMode
          ? CyberResilemce_OnPrem_img
          : CyberResilemce_OnPrem_img_lite,
        data: allData,
        series: allMiniSeries,
        score: getLatestScore(allData),
      },
      onPrem: {
        name: "On-prem",
        icon: isDarkMode
          ? CyberResilemce_OnPrem_img
          : CyberResilemce_OnPrem_img_lite,
        data: onPremData,
        series: onPremMiniSeries,
        score: getLatestScore(onPremData),
      },
      // image here
      cloud: {
        name: "Cloud",
        icon: isDarkMode ? Cloud_OnPrem_dark_img : Cloud_OnPrem_img,
        data: cloudData,
        series: cloudMiniSeries,
        score: getLatestScore(cloudData),
      },
      cve: {
        name: "CVE",
        icon: isDarkMode ? Cve_OnPrem_dark_img : Cve_OnPrem_img,
        data: cveData,
        series: cveMiniSeries,
        score: getLatestScore(cveData),
      },
    };
    return sectionMap[section as keyof typeof sectionMap];
  };

  // Helper function to check if there are any valid datasets to show
  const hasAnyValidDatasets = () => {
    const datasets = ["all", "onPrem", "cloud", "cve"];
    return datasets;
    //return datasets.some((dataset) => hasSectionData(dataset));
  };

  const getTimeRangeDisplayText = (): string => {
    if (appliedFromDate && appliedToDate) {
      return `${appliedFromDate.format("DD MMM YYYY")} - ${appliedToDate.format("DD MMM YYYY")}`;
    }
    return "Select date range";
  };

  // Header icon buttons match the date range select: 32px, outlined, 4px radius
  const headerIconButtonSx = {
    width: 32,
    height: 32,
    borderRadius: "4px",
    border: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)"}`,
    color: theme.palette.text.secondary,
    "&:hover": {
      borderColor: "#FF6B00",
      backgroundColor: "rgba(255, 107, 0, 0.08)",
    },
  };

  // Full-widget skeleton only for the first load; later reloads (dataset,
  // date range, filters) keep the widget and show a skeleton in the chart area
  if (loading && !showContent) {
    return (
      <Box height="100%">
        <Paper
          sx={{
            p: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor:
              theme.palette.mode === "dark" ? "background.paper" : "white",
            // boxShadow:
            //   theme.palette.mode === "dark"
            //     ? "0px 4px 12px rgba(0, 0, 0, 0.3)"
            //     : "0px 4px 12px rgba(0, 0, 0, 0.1)",
            borderRadius: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={200} height={32} />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Box sx={{ position: "relative", height: 300 }}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  sx={{ borderRadius: 1 }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    right: 16,
                    display: "flex",
                    gap: 2,
                  }}
                >
                  {[1, 2, 3].map((index) => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      width={80}
                      height={40}
                      sx={{ borderRadius: 1 }}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                {[1, 2, 3].map((index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Skeleton variant="circular" width={32} height={32} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Skeleton variant="text" width="60%" height={20} />
                        <Skeleton variant="text" width="40%" height={16} />
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="circular" width={8} height={8} />
                      <Skeleton variant="text" width="30%" height={20} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  }

  if (!showContent) {
    return null;
  }

  return (
    <>
      <Box
        className={styles.container}
        sx={{
          p: 0,
          bgcolor: isDarkMode ? "background.paper" : "white",
          borderRadius: "4px",
          overflow: "visible",
          border: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"
            }`,
          m: 0,
          pb: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 0.5,
            borderBottom: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.12)" : "#f0f0f0"
              }`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* <img
              src={theme.palette.mode === "dark" ? darklogo : lightlogo}
              alt="CyberResilienceView"
              style={{
                height: "35px",
                verticalAlign: "middle",
                marginLeft: theme.palette.mode === "dark" ? 10 : 0
              }}
            /> */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                height: "35px",
                ml: 2,
                display: "flex",
                alignItems: "center",
              }}
            >
              Resilience
            </Typography>
          </Box>
          {/* Date range and filter change what the graph shows; download acts on it */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <Select
                open={selectOpen}
                onOpen={() => {
                  setFromDate(appliedFromDate);
                  setToDate(appliedToDate);
                  setSelectOpen(true);
                }}
                onClose={() => {
                  // 🔥 RESET if Apply not clicked
                  setFromDate(appliedFromDate);
                  setToDate(appliedToDate);
                  setSelectOpen(false);
                }}
                value={timeRange}
                renderValue={() => getTimeRangeDisplayText()}
                sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  height: '32px',
                }}
              >
                {/* ✅ ONE MenuItem containing custom UI */}
                <MenuItem disableRipple disableTouchRipple>
                  <Box onClick={(e) => e.stopPropagation()}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Stack spacing={2} width={500}>
                        <Box display="flex" gap={2}>
                          <DatePicker
                            label="From"
                            value={fromDate}
                            minDate={firstDataDate || undefined}
                            maxDate={toDate || dayjs()}
                            onChange={setFromDate}
                            open={openFrom}
                            onOpen={() => {
                              setOpenFrom(true);
                              setOpenTo(false);   // 🔥 close To picker
                            }}
                            onClose={() => setOpenFrom(false)}
                            slotProps={{ textField: { size: "small" } }}
                          />

                          <DatePicker
                            label="To"
                            value={toDate}
                            minDate={fromDate || firstDataDate || undefined}
                            maxDate={dayjs()}
                            onChange={setToDate}
                            open={openTo}
                            onOpen={() => {
                              setOpenTo(true);
                              setOpenFrom(false); // 🔥 close From picker
                            }}
                            onClose={() => setOpenTo(false)}
                            slotProps={{ textField: { size: "small" } }}
                          />

                          <Button
                            variant="contained"
                            size="small"
                            disabled={isInvalidRange}
                            onClick={() => {
                              setAppliedFromDate(fromDate);
                              setAppliedToDate(toDate);
                              setStartDate(fromDate);
                              setEndDate(toDate);
                              setSelectOpen(false);
                            }}
                            sx={{ textTransform: "none", fontWeight: 600 }}
                          >
                            Apply
                          </Button>
                        </Box>
                      </Stack>
                    </LocalizationProvider>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            {hasAnyValidDatasets() && (
              <Tooltip title="Filter" placement="top">
                <IconButton
                  onClick={handleFilterClick}
                  aria-label="Filter"
                  className="no-drag clickable-area"
                  sx={{
                    ...headerIconButtonSx,
                    ...(showMiniGraphsInPopover && {
                      borderColor: "#FF6B00",
                      backgroundColor: "rgba(255, 107, 0, 0.1)",
                    }),
                  }}
                >
                  <img
                    src={funnel_filter}
                    alt=""
                    style={{ width: "18px", height: "18px" }}
                  />
                </IconButton>
              </Tooltip>
            )}
            {hasPermissions(ROLES_CONTENT_PROPS.DOWNLOAD_REPORT) && (
              <Tooltip title="Download report" placement="top">
                <IconButton
                  onClick={handleDownload}
                  aria-label="Download report"
                  className="no-drag clickable-area"
                  sx={headerIconButtonSx}
                >
                  <DownloadIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* <Box
              className="no-drag clickable-area"
              onClick={() => setChatOpen(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                color: isDarkMode ? "#b3b3b3" : "#6B7280",
                // border: `1px solid ${
                //   isDarkMode ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"
                // }`,
                // borderRadius: "50%",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  // borderColor: isDarkMode ? "#FF6B00" : "#1976d2",
                  color: isDarkMode ? "#FF6B00" : "#1976d2",
                  bgcolor: isDarkMode
                    ? "rgba(255, 107, 0, 0.1)"
                    : "rgba(25, 118, 210, 0.1)",
                },
              }}
            >
              <img
                src={chat_icon}
                alt="Chat"
                style={{
                  width: "24px",
                  height: "24px",
                  filter: isDarkMode ? "brightness(0.8)" : "none",
                }}
              />
            </Box> */}
          </Box>
        </Box>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleDateClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {selectionStep === "start"
                ? "Select start date"
                : "Select end date"}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={selectionStep === "start" ? tempStartDate : tempEndDate}
                onChange={handleDateSelect}
                maxDate={dayjs()}
                minDate={
                  selectionStep === "end"
                    ? tempStartDate || firstDataDate || undefined
                    : firstDataDate || undefined
                }
                views={["day"]}
                slots={{
                  day: (props) => (
                    <CustomDay
                      {...props}
                      tempStartDate={tempStartDate}
                      tempEndDate={tempEndDate}
                      theme={theme}
                    />
                  ),
                }}
              />
            </LocalizationProvider>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                borderTop: 1,
                borderColor: "divider",
                pt: 2,
                mt: 2,
              }}
            >
              <Button
                onClick={handleDateApply}
                variant="contained"
                // Disabled until both temp dates are selected AND at least one of them
                // differs from the currently applied start/end dates.
                disabled={
                  !tempStartDate ||
                  !tempEndDate ||
                  (startDate &&
                    endDate &&
                    tempStartDate.isSame(startDate, "day") &&
                    tempEndDate.isSame(endDate, "day"))
                }
                sx={{
                  textTransform: "none",
                  px: 3,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
              width: 80,
              maxHeight: 280,
              p: 1,
              overflow: "visible",
            },
          }}
        >
          <Box
            sx={{
              p: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {/* Theme-aware scroll styling */}
            <style>
              {`
                .cyber-resilience-filter-scroll::-webkit-scrollbar {
                  width: 6px;
                }
                .cyber-resilience-filter-scroll::-webkit-scrollbar-track {
                  background: ${isDarkMode
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)"
                };
                  border-radius: 3px;
                }
                .cyber-resilience-filter-scroll::-webkit-scrollbar-thumb {
                  background: ${isDarkMode
                  ? "rgba(255, 255, 255, 0.3)"
                  : "rgba(0, 0, 0, 0.3)"
                };
                  border-radius: 3px;
                }
                .cyber-resilience-filter-scroll::-webkit-scrollbar-thumb:hover {
                  background: ${isDarkMode
                  ? "rgba(255, 255, 255, 0.5)"
                  : "rgba(0, 0, 0, 0.5)"
                };
                }
              `}
            </style>
            <Box
              className="cyber-resilience-filter-scroll"
              sx={{
                p: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                maxHeight: "100%",
                overflowY: "auto",
              }}
            >
              {["all", "onPrem", "cloud", "cve"].map((section) => {
                const sectionData = getSectionData(section);
                // Only show sections that have data (score > 0)
                // if (!hasSectionData(section)) {
                //   return null;
                // }
                return (
                  <Box
                    key={section}
                    onClick={() => {
                      handleDatasetChange(section);
                      handleFilterClose();
                    }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      mb: 1,
                      transition: "all 0.2s",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        border: "1.5px solid #BDBDF5",
                        background: "white",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "#6366F1",
                          background: "#F3F4FD",
                        },
                      }}
                    >
                      <img
                        src={sectionData.icon}
                        alt={sectionData.name}
                        style={{ width: 20, height: 20 }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 500,
                        color: "#7C7BA0",
                        mt: 0.5,
                        textAlign: "center",
                        lineHeight: 1,
                      }}
                    >
                      {sectionData.name}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Popover>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            height: "220px",
            position: "relative",
            m: 0,
            p: 0,
            overflow: "visible",
          }}
        >
          {/* Main Graph Only */}
          <Box
            sx={{
              width: "100%",
              p: 0,
              height: "100%",
              position: "relative",
              overflow: "visible",
            }}
          >
            {/* Dataset Indicator */}
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: isDarkMode
                  ? "rgba(0, 0, 0, 0.7)"
                  : "rgba(255, 255, 255, 0.9)",
                borderRadius: "4px",
                px: 1.5,
                py: 0.5,
                border: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)"
                  }`,
              }}
            >
              <img
                src={getSectionData(activeDataset).icon}
                alt={getSectionData(activeDataset).name}
                style={{
                  width: 16,
                  height: 16,
                  filter: isDarkMode ? "brightness(0.8)" : "none",
                }}
              />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: isDarkMode ? "#fff" : "#333",
                  textTransform: "capitalize",
                }}
              >
                {getSectionData(activeDataset).name}
              </Typography>
            </Box>

            <Box
              className="clickable-area"
              sx={{
                height: "100%",
                position: "relative",
                overflow: "visible",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  opacity: isChartLoading ? 0.45 : 1,
                  filter: isChartLoading ? "blur(1.5px) grayscale(40%)" : "none",
                  transition: "opacity 200ms ease, filter 200ms ease",
                  pointerEvents: isChartLoading ? "none" : "auto",
                }}
              >
                <ReactApexChart
                  key={chartKey}
                  options={shownChartRef.current.options}
                  series={shownChartRef.current.series}
                  type="area"
                  height="100%"
                />
              </Box>
              {isChartLoading && (
                <Box
                  aria-live="polite"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: 1,
                    overflow: "hidden",
                    pointerEvents: "none",
                  }}
                >
                  <LinearProgress
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 2,
                      bgcolor: "transparent",
                      "& .MuiLinearProgress-bar": { bgcolor: "#FF6B00" },
                    }}
                  />
                  {/* Soft brand-coloured sweep across the chart area */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background: `linear-gradient(100deg, transparent 25%, ${isDarkMode
                        ? "rgba(255, 107, 0, 0.10)"
                        : "rgba(255, 107, 0, 0.08)"
                        } 50%, transparent 75%)`,
                      transform: "translateX(-100%)",
                      "@keyframes crLoadingSweep": {
                        "100%": { transform: "translateX(100%)" },
                      },
                      animation: "crLoadingSweep 1.6s ease-in-out infinite",
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                        transform: "none",
                      },
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 2,
                        py: 1.25,
                        borderRadius: 2,
                        bgcolor: isDarkMode
                          ? "rgba(30, 30, 30, 0.75)"
                          : "rgba(255, 255, 255, 0.85)",
                        backdropFilter: "blur(6px)",
                        border: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
                        boxShadow: isDarkMode
                          ? "0 8px 24px rgba(0, 0, 0, 0.45)"
                          : "0 8px 24px rgba(0, 0, 0, 0.08)",
                      }}
                    >
                      <img
                        src={getSectionData(activeDataset).icon}
                        alt=""
                        style={{ width: 20, height: 20 }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            lineHeight: 1.3,
                            color: isDarkMode ? "#fff" : "#1a1a1a",
                          }}
                        >
                          Loading {getSectionData(activeDataset).name} trend
                        </Typography>
                        {startDate && endDate && (
                          <Typography
                            sx={{
                              fontSize: "11px",
                              lineHeight: 1.3,
                              color: isDarkMode ? "#b3b3b3" : "#666",
                            }}
                          >
                            {startDate.format("MMM D")} – {endDate.format("MMM D, YYYY")}
                          </Typography>
                        )}
                      </Box>
                      {/* A small trend line that draws itself on a loop */}
                      <Box
                        component="svg"
                        viewBox="0 0 48 20"
                        sx={{
                          width: 48,
                          height: 20,
                          overflow: "visible",
                          "& path": {
                            strokeDasharray: 1,
                            strokeDashoffset: 1,
                            animation: "crLoadingDraw 1.4s ease-in-out infinite",
                          },
                          "@keyframes crLoadingDraw": {
                            "0%": { strokeDashoffset: 1, opacity: 1 },
                            "60%": { strokeDashoffset: 0, opacity: 1 },
                            "85%": { strokeDashoffset: 0, opacity: 0 },
                            "100%": { strokeDashoffset: 1, opacity: 0 },
                          },
                          "@media (prefers-reduced-motion: reduce)": {
                            "& path": { animation: "none", strokeDashoffset: 0 },
                          },
                        }}
                      >
                        <path
                          d="M1 15 L9 11 L16 13 L24 6 L31 9 L39 3 L47 5"
                          pathLength={1}
                          fill="none"
                          stroke="#FF6B00"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        {/* Static counts below the graph */}
        {/* <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 5,
            mt: 0,
            mb: 0,
            width: "100%",
            p: 0,
          }}
        >
          {["onPrem", "cloud", "cve"].map((section) => {
            const sectionData = getSectionData(section);
            // Only show sections that have data (score > 0)
            if (!hasSectionData(section)) {
              return null;
            }
            return (
              <Box
                key={section}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <img
                  src={sectionData.icon}
                  alt={sectionData.name}
                  style={{
                    width: 18,
                    height: 18,
                    marginRight: 4,
                    verticalAlign: "middle",
                    filter: "grayscale(0.5)",
                  }}
                />
                <Typography
                  sx={{ fontSize: 13, color: "#7C7BA0", fontWeight: 500 }}
                >
                  {sectionData.name} :
                </Typography>
                <Typography
                  sx={{
                    fontSize: 15,
                    color: "#6366F1",
                    fontWeight: 700,
                    ml: 0.5,
                  }}
                >
                  {sectionData.score}
                </Typography>
              </Box>
            );
          })}
        </Box> */}
      </Box>

      <Drawer
        anchor="right"
        open={eventClicked}
        onClose={() => setEventClicked(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: "50%" },
            bgcolor: isDarkMode ? "background.paper" : "white",
            boxShadow: "-4px 0 8px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() => {
                  setEventClicked(false);
                  setEventListOpen(true);
                }}
                size="small"
              >
                <ArrowBackIcon />
              </IconButton>
              <EventIcon color="primary" />
              <Typography variant="h6">Event Details</Typography>
              {(selectedEvent?.event_code === "new_scan" ||
                selectedEvent?.event_code === "rescan" ||
                selectedEvent?.event_code === "cloud") && (
                  <img
                    src={Layer123}
                    alt="Layer123"
                    style={{ height: 22, cursor: "pointer" }}
                    onClick={() => {
                      if (
                        selectedEvent?.event_code === "new_scan" ||
                        selectedEvent?.event_code === "rescan"
                      ) {
                        if (selectedEvent?.referenceID) {
                          navigate(`/onprem/scan/${selectedEvent.referenceID}`);
                        }
                      } else if (selectedEvent?.event_code === "cloud") {
                        navigate(`/cloud/scan/${selectedEvent.referenceID}`);
                      }
                    }}
                  />
                )}
            </Box>
            <IconButton
              onClick={() => {
                setEventClicked(false);
                setEventListOpen(false);
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Event Content */}
          <Box
            sx={{
              p: 0,
              flexGrow: 1,
              overflowY: "auto",
            }}
          >
            <CyberResilienceRediretions event={selectedEvent} />
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={chatOpen}
        onClose={handleClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: "40%" },
            bgcolor: isDarkMode ? "background.paper" : "white",
            boxShadow: "-4px 0 8px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {chatResponse.data?.result && (
                <Tooltip title="Back to welcome" placement="bottom">
                  <IconButton
                    onClick={handleBackToWelcome}
                    size="small"
                    sx={{
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        color: theme.palette.text.primary,
                        bgcolor: isDarkMode
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                </Tooltip>
              )}
              <img
                src={theme.palette.mode === "dark" ? darklogo : lightlogo}
                alt="CyberResilienceView"
                style={{ height: "35px", verticalAlign: "middle" }}
              />
            </Box>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Chat Content */}
          <Box
            sx={{
              p: 2,
              flexGrow: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 2,
            }}
          >
            {isLoadingChat ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress size={40} />
                <Typography variant="body1" color="text.secondary">
                  Processing your request...
                </Typography>
              </Box>
            ) : !chatResponse.data?.result ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  maxWidth: "800px",
                  margin: "0 auto",
                  width: "100%",
                }}
              >
                {/* Welcome Message */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    mb: 4,
                  }}
                >
                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={chat_icon}
                      alt="Chat"
                      style={{
                        width: "48px",
                        height: "48px",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      fontSize: "1.1rem",
                    }}
                  >
                    Hi, I am your security assistant. How can I help you today?
                  </Typography>
                </Box>

                {/* Quick Action Cards */}
                <Box sx={{ width: "100%" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                          },
                        }}
                      >
                        <CardActionArea
                          onClick={() =>
                            handleCardClick(
                              "List all scans that reduced the score in last 6 months"
                            )
                          }
                          sx={{
                            p: 2,
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              textAlign: "center",
                              color: "text.secondary",
                            }}
                          >
                            List all scans that reduced the score in last 6
                            months
                          </Typography>
                        </CardActionArea>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                          },
                        }}
                      >
                        <CardActionArea
                          onClick={() =>
                            handleCardClick(
                              "How many new scan events happens in the last 10 days"
                            )
                          }
                          sx={{
                            p: 2,
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              textAlign: "center",
                              color: "text.secondary",
                            }}
                          >
                            How many new scan events happens in the last 10 days
                          </Typography>
                        </CardActionArea>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                          },
                        }}
                      >
                        <CardActionArea
                          onClick={() =>
                            handleCardClick(
                              "How many decommissioned events in the January"
                            )
                          }
                          sx={{
                            p: 2,
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              textAlign: "center",
                              color: "text.secondary",
                            }}
                          >
                            How many decommissioned events in the January
                          </Typography>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  width: "100%",
                }}
              >
                {/* Total Count Box - Only show when there's actual data */}
                {extractTotalCount(chatResponse.data.result) &&
                  !hasNoData(chatResponse.data.result) && (
                    <Box
                      sx={{
                        bgcolor: isDarkMode
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)",
                        p: 2,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                      }}
                    >
                      <SecurityIcon
                        sx={{ color: theme.palette.primary.main, mt: 0.5 }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 500,
                          lineHeight: 1.5,
                        }}
                      >
                        {extractTotalCount(chatResponse.data.result)}
                      </Typography>
                    </Box>
                  )}

                {/* No Data Message - Show only when no data */}
                {hasNoData(chatResponse.data.result) && (
                  <Box
                    sx={{
                      bgcolor: isDarkMode
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.02)",
                      p: 3,
                      borderRadius: 2,
                      border: `1px solid ${isDarkMode
                        ? "rgba(147, 112, 219, 0.1)"
                        : "rgba(147, 112, 219, 0.15)"
                        }`,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      gap: 2,
                      maxWidth: "600px",
                      mx: "auto",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: isDarkMode
                          ? "rgba(147, 112, 219, 0.1)"
                          : "rgba(147, 112, 219, 0.05)",
                        p: 2,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AssessmentIcon
                        sx={{
                          color: isDarkMode
                            ? "rgba(147, 112, 219, 0.9)"
                            : "rgba(147, 112, 219, 1)",
                          fontSize: "32px",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        No Data Found
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        No matching events were found for your query. Try
                        adjusting your search criteria or time range.
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Table Section - Only show if table data exists and not no-data */}
                {hasTableData(chatResponse.data.result) &&
                  !hasNoData(chatResponse.data.result) && (
                    <Paper
                      elevation={0}
                      sx={{
                        width: "60%",
                        ml: 0,
                        borderRadius: 2,
                        overflow: "hidden",
                        bgcolor: isDarkMode
                          ? "rgba(255, 255, 255, 0.05)"
                          : "#F8F9FA",
                        border: `1px solid ${isDarkMode
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.1)"
                          }`,
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                bgcolor: isDarkMode
                                  ? "rgba(147, 112, 219, 0.1)"
                                  : "rgba(147, 112, 219, 0.05)",
                                borderBottom: `1px solid ${isDarkMode
                                  ? "rgba(147, 112, 219, 0.2)"
                                  : "rgba(147, 112, 219, 0.1)"
                                  }`,
                                color: isDarkMode
                                  ? "rgba(147, 112, 219, 0.9)"
                                  : "rgba(147, 112, 219, 1)",
                                py: 2.5,
                                height: "56px",
                              }}
                            >
                              Last Updated
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                bgcolor: isDarkMode
                                  ? "rgba(147, 112, 219, 0.1)"
                                  : "rgba(147, 112, 219, 0.05)",
                                borderBottom: `1px solid ${isDarkMode
                                  ? "rgba(147, 112, 219, 0.2)"
                                  : "rgba(147, 112, 219, 0.1)"
                                  }`,
                                color: isDarkMode
                                  ? "rgba(147, 112, 219, 0.9)"
                                  : "rgba(147, 112, 219, 1)",
                                py: 2.5,
                                height: "56px",
                              }}
                            >
                              Accepted Risk
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        {chatResponse?.data?.result ? (
                          <ChatResponseTable
                            data={chatResponse.data.result}
                            isDarkMode={isDarkMode}
                            theme={theme}
                          />
                        ) : null}
                      </Table>
                    </Paper>
                  )}

                {/* Summary Box - Show after table when there's table data, or standalone when no table, but not when no data */}
                {chatResponse?.data?.result &&
                  hasSummary(chatResponse.data.result) &&
                  !hasNoData(chatResponse.data.result) && (
                    <Box
                      sx={{
                        bgcolor: isDarkMode
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)",
                        p: 2,
                        borderRadius: 1,
                        mt: hasTableData(chatResponse.data.result) ? 2 : 0,
                        border: `1px solid ${isDarkMode
                          ? "rgba(147, 112, 219, 0.1)"
                          : "rgba(147, 112, 219, 0.15)"
                          }`,
                        display: "flex",
                        gap: 2,
                        alignItems: "flex-start",
                        width: hasTableData(chatResponse.data.result)
                          ? "60%"
                          : "auto",
                        maxWidth: hasTableData(chatResponse.data.result)
                          ? "60%"
                          : "800px",
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: isDarkMode
                            ? "rgba(147, 112, 219, 0.1)"
                            : "rgba(147, 112, 219, 0.05)",
                          p: 1,
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <SecurityIcon
                          sx={{
                            color: isDarkMode
                              ? "rgba(147, 112, 219, 0.9)"
                              : "rgba(147, 112, 219, 1)",
                            fontSize: "20px",
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: isDarkMode
                              ? "rgba(147, 112, 219, 0.9)"
                              : "rgba(147, 112, 219, 1)",
                            mb: 0.5,
                            fontWeight: 600,
                          }}
                        >
                          Summary
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                            lineHeight: 1.6,
                          }}
                        >
                          {extractSummary(chatResponse.data.result)}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                {/* Text Response Section - Show when no table data and not no-data */}
                {!hasTableData(chatResponse.data.result) &&
                  !hasNoData(chatResponse.data.result) && (
                    <Box
                      sx={{
                        bgcolor: isDarkMode
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)",
                        p: 3,
                        borderRadius: 2,
                        border: `1px solid ${isDarkMode
                          ? "rgba(147, 112, 219, 0.1)"
                          : "rgba(147, 112, 219, 0.15)"
                          }`,
                        display: "flex",
                        gap: 2,
                        alignItems: "flex-start",
                        maxWidth: "800px",
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: isDarkMode
                            ? "rgba(147, 112, 219, 0.1)"
                            : "rgba(147, 112, 219, 0.05)",
                          p: 1.5,
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <AssessmentIcon
                          sx={{
                            color: isDarkMode
                              ? "rgba(147, 112, 219, 0.9)"
                              : "rgba(147, 112, 219, 1)",
                            fontSize: "24px",
                          }}
                        />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            color: theme.palette.text.primary,
                            lineHeight: 1.7,
                            fontSize: "0.95rem",
                            whiteSpace: "pre-line",
                          }}
                        >
                          {chatResponse.data.result}
                        </Typography>
                      </Box>
                    </Box>
                  )}
              </Box>
            )}
          </Box>

          {/* Input Area */}
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: isDarkMode ? "background.paper" : "grey.50",
              mt: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: isDarkMode ? "rgba(147, 112, 219, 0.03)" : "#FFFFFF",
                borderRadius: 2,
                border: `1px solid ${isDarkMode
                  ? "rgba(147, 112, 219, 0.1)"
                  : "rgba(147, 112, 219, 0.15)"
                  }`,
                py: 0.5,
                px: 1.5,
                "&:hover": {
                  bgcolor: isDarkMode
                    ? "rgba(147, 112, 219, 0.05)"
                    : "rgba(147, 112, 219, 0.02)",
                  border: `1px solid ${isDarkMode
                    ? "rgba(147, 112, 219, 0.2)"
                    : "rgba(147, 112, 219, 0.3)"
                    }`,
                },
                transition: "all 0.2s ease",
                minHeight: "40px",
                boxShadow: isDarkMode
                  ? "none"
                  : "0 2px 4px rgba(147, 112, 219, 0.05)",
              }}
            >
              <TextField
                fullWidth
                placeholder="Type your question here..."
                variant="standard"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                InputProps={{
                  disableUnderline: true,
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    color: isDarkMode
                      ? "rgba(147, 112, 219, 0.9)"
                      : "rgba(147, 112, 219, 1)",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiInputBase-input": {
                    padding: "0 4px",
                    height: "32px",
                    lineHeight: "32px",
                    fontSize: "14px",
                    "&::placeholder": {
                      color: isDarkMode
                        ? "rgba(147, 112, 219, 0.5)"
                        : "rgba(147, 112, 219, 0.4)",
                      opacity: 1,
                      fontWeight: 400,
                    },
                  },
                }}
              />
              <IconButton
                onClick={handleInputSubmit}
                disabled={isLoadingChat}
                size="medium"
                sx={{
                  color: isDarkMode
                    ? "rgba(147, 112, 219, 0.9)"
                    : "rgba(147, 112, 219, 0.8)",
                  "&:hover": {
                    bgcolor: isDarkMode
                      ? "rgba(147, 112, 219, 0.1)"
                      : "rgba(147, 112, 219, 0.05)",
                    color: isDarkMode
                      ? "rgba(147, 112, 219, 1)"
                      : "rgba(147, 112, 219, 1)",
                  },
                  padding: "4px",
                  transition: "all 0.2s ease",
                }}
              >
                {isLoadingChat ? (
                  <CircularProgress size={20} sx={{ color: "inherit" }} />
                ) : (
                  <img
                    src={chat_send}
                    alt="Send"
                    style={{
                      width: "24px",
                      height: "24px",
                      opacity: isLoadingChat ? 0.7 : 1,
                      transition: "opacity 0.2s ease",
                    }}
                  />
                )}
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={eventDialogOpen}
        onClose={() => setEventDialogOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: "50%" },
            bgcolor: theme.palette.background.paper,
            boxShadow: "-4px 0 8px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <EventIcon color="primary" />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Security Events - {selectedEventData?.date || ""}
            </Typography>
            <IconButton
              onClick={() => setEventDialogOpen(false)}
              size="small"
              sx={{
                color: "text.secondary",
                "&:hover": { color: "text.primary" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2 }} className="scroll">
            {eventDetailsLoading ? (
              <Stack spacing={2}>
                {[1, 2, 3].map((_, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <Skeleton variant="text" width={80} height={24} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Skeleton variant="text" width={60} height={24} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Skeleton variant="text" width={60} height={24} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Skeleton variant="text" width={100} height={24} />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            ) : (
              selectedEventData && (
                <Stack spacing={2}>
                  {selectedEventData.data?.map((event: any, index: number) => {
                    // Simplify event type display
                    const simplifiedEventType = event.event_type
                      .split("-")[0]
                      .trim();
                    return (
                      <Card
                        key={index}
                        elevation={0}
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 2,
                          cursor: isEventClickable(event) ? "pointer" : "default",
                          transition: "transform 0.2s, box-shadow 0.2s",
                          "&:hover": isEventClickable(event) ? {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                          } : {},

                        }}
                        onClick={isEventClickable(event) ? () => handleEventCardClick(event) : undefined}
                      >
                        <Box sx={{ p: 2 }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <TimelineIcon
                                  color="action"
                                  sx={{ fontSize: 20 }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {event.time}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <AssessmentIcon
                                  color="primary"
                                  sx={{ fontSize: 20 }}
                                />
                                <Typography variant="body1" fontWeight="medium">
                                  {event.security_score}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {parseFloat(event.score_change) > 0 ? (
                                  <>
                                    <TrendingUpIcon
                                      sx={{
                                        color: "success.main",
                                        fontSize: 20,
                                      }}
                                    />
                                    <Typography
                                      color="success.main"
                                      fontWeight="medium"
                                    >
                                      +{event.score_change}
                                    </Typography>
                                  </>
                                ) : parseFloat(event.score_change) < 0 ? (
                                  <>
                                    <TrendingDownIcon
                                      sx={{ color: "error.main", fontSize: 20 }}
                                    />
                                    <Typography
                                      color="error.main"
                                      fontWeight="medium"
                                    >
                                      {event.score_change}
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <RemoveIcon
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: 20,
                                      }}
                                    />
                                    <Typography color="text.secondary">
                                      No change
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  bgcolor: isDarkMode
                                    ? "rgba(147, 112, 219, 0.1)"
                                    : "rgba(147, 112, 219, 0.05)",
                                  borderRadius: 1,
                                  px: 1.5,
                                  py: 0.75,
                                  width: "fit-content",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isDarkMode
                                      ? "rgba(147, 112, 219, 0.9)"
                                      : "rgba(147, 112, 219, 1)",
                                    fontWeight: 500,
                                    fontSize: "10px",
                                    maxWidth: "150px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    title: { simplifiedEventType },
                                  }}
                                >
                                  {simplifiedEventType}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Card>
                    );
                  })}
                </Stack>
              )
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Event List Drawer */}
      <Drawer
        anchor="right"
        open={eventListOpen}
        onClose={() => setEventListOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: "40%" },
            bgcolor: isDarkMode ? "background.paper" : "white",
            boxShadow: "-4px 0 8px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={() => setEventListOpen(false)} size="small">
                <ArrowBackIcon />
              </IconButton>
              <EventIcon color="primary" />
              <Typography variant="h6">
                Events on {eventListData?.date}
              </Typography>
            </Box>
            <IconButton onClick={() => setEventListOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Event List Content */}
          <Box
            sx={{
              p: 2,
              flexGrow: 1,
              overflowY: "auto",
            }}
          >
            <style>
              {`
                .event-list-scroll::-webkit-scrollbar {
                  width: 8px;
                }
                .event-list-scroll::-webkit-scrollbar-track {
                  background: ${isDarkMode
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.05)"
                };
                  border-radius: 4px;
                }
                .event-list-scroll::-webkit-scrollbar-thumb {
                  background: ${isDarkMode
                  ? "rgba(99, 102, 241, 0.3)"
                  : "rgba(99, 102, 241, 0.2)"
                };
                  border-radius: 4px;
                }
                .event-list-scroll::-webkit-scrollbar-thumb:hover {
                  background: ${isDarkMode
                  ? "rgba(99, 102, 241, 0.5)"
                  : "rgba(99, 102, 241, 0.4)"
                };
                }
              `}
            </style>
            <Box
              className="event-list-scroll"
              sx={{ height: "100%", overflowY: "auto" }}
            >
              {eventListData?.data?.map((event: any, index: number) => {

                const getEventType = (eventCode: string, eventName: string) => {
                  if (!eventCode || !eventName) return "Event";
                  if (eventCode === "new_scan") return "New Scan";
                  if (eventCode === "rescan") return "Re Scan";
                  if (eventName.includes("Risk Accepted"))
                    return "Risk Accepted";
                  if (eventName.includes("Risk Unaccepted"))
                    return "Risk Unaccepted";
                  if (eventName.includes("Device Decommissioned"))
                    return "Device Decommissioned";
                  if (eventName.includes("Device Deleted"))
                    return "Device Deleted";
                  if (eventName.includes("Master file Update"))
                    return "Master file Update";
                  if (eventName.includes("Asset Critacality"))
                    return "Asset Critacality";
                  return eventName.split("-")[0].trim();
                };

                const eventType = getEventType(
                  event.event_code,
                  event.event_name || event.event_type
                );
                const scoreChange = event.score_change;
                const hasScore = scoreChange !== 0;

                // Get event icon based on event type
                const getEventIcon = (eventCode: string, eventName: string) => {
                  if (!eventCode || !eventName) return <EventIcon />;
                  if (eventCode === "new_scan" || eventCode === "rescan")
                    return <SecurityIcon />;
                  if (eventName.includes("Risk")) return <TrendingUpIcon />;
                  if (eventName.includes("Device")) return <TimelineIcon />;
                  return <EventIcon />;
                };

                // Get event color based on event type
                const getEventColor = (
                  eventCode: string,
                  eventName: string
                ) => {
                  if (!eventCode || !eventName) return "#6366f1";
                  if (eventCode === "new_scan" || eventCode === "rescan")
                    return "#10b981";
                  if (eventName.includes("Risk Accepted")) return "#f59e0b";
                  if (eventName.includes("Risk Unaccepted")) return "#ef4444";
                  if (eventName.includes("Device")) return "#8b5cf6";
                  return "#6366f1";
                };

                const eventIcon = getEventIcon(
                  event.event_code,
                  event.event_name || event.event_type
                );
                const eventColor = getEventColor(
                  event.event_code,
                  event.event_name || event.event_type
                );

                return (
                  <Card
                    key={index}
                    onClick={isEventClickable(event) ? () => handleEventItemClick(event) : undefined}
                    sx={{
                      mb: 2,
                      cursor: isEventClickable(event) ? "pointer" : "default",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                    }}
                  >
                    <CardActionArea
                      sx={{
                        cursor: isEventClickable(event) ? "pointer" : "default",
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                p: 0.5,
                                borderRadius: 1,
                                bgcolor: `${eventColor}15`,
                              }}
                            >
                              {React.cloneElement(eventIcon, {
                                sx: {
                                  color: eventColor,
                                  fontSize: 18,
                                },
                              })}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {event.time}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ color: eventColor }}
                          >
                            {eventType}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          {hasScore && (
                            <>
                              <Typography variant="body2">
                                Score: {event.security_score}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                {scoreChange > 0 ? (
                                  <TrendingUpIcon
                                    sx={{ color: "success.main", fontSize: 16 }}
                                  />
                                ) : (
                                  <TrendingDownIcon
                                    sx={{ color: "error.main", fontSize: 16 }}
                                  />
                                )}
                                <Typography
                                  variant="body2"
                                  color={
                                    scoreChange > 0
                                      ? "success.main"
                                      : "error.main"
                                  }
                                  fontWeight="medium"
                                >
                                  {scoreChange > 0 ? "+" : ""}
                                  {scoreChange}
                                </Typography>
                              </Box>
                            </>
                          )}
                        </Box>
                      </Box>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

const CustomDay: React.FC<CustomDayProps> = ({
  day,
  tempStartDate,
  tempEndDate,
  theme,
  ...other
}) => {
  const isInRange =
    tempStartDate &&
    tempEndDate &&
    day.isAfter(tempStartDate, "day") &&
    day.isBefore(tempEndDate, "day");

  const isSelected =
    (tempStartDate && day.isSame(tempStartDate, "day")) ||
    (tempEndDate && day.isSame(tempEndDate, "day"));

  return (
    <PickersDay
      day={day}
      {...other}
      sx={{
        ...(isInRange && {
          backgroundColor: `${theme.palette.primary.main}33`,
          color: theme.palette.text.primary,
          "&:hover": {
            backgroundColor: `${theme.palette.primary.main}4D`,
          },
        }),
        ...(isSelected && {
          backgroundColor: `${theme.palette.primary.main} !important`,
          color: `${theme.palette.common.white} !important`,
          opacity: "1 !important",
          fontWeight: "bold",
          "&:hover": {
            backgroundColor: `${theme.palette.primary.dark} !important`,
          },
        }),
        borderRadius: isInRange ? "0" : "50%",
        "&:first-of-type": {
          borderTopLeftRadius: "50%",
          borderBottomLeftRadius: "50%",
        },
        "&:last-of-type": {
          borderTopRightRadius: "50%",
          borderBottomRightRadius: "50%",
        },
      }}
    />
  );
};

export default CyberResilienceView;



